// bulbal: cuộc gọi/họp nhóm qua Cloudflare RealtimeKit — THAY THẾ call machinery gốc Tinode.
// Flow: POST HRM /api/tinode/call/create → {meetingId, meetingUrl} → publish tin nhắn drafty
// có link mời vào topic (1-1 lẫn NHÓM cùng một flow) → window.open(meetingUrl).
// "Đổ chuông" = chính tin nhắn mời + FCM push; KHÔNG dùng {info call}/webrtc head của Tinode.
// UI cuộc gọi nằm ở hrm.bulbalvn.com/call/<id> (RtkMeeting) — KHÔNG bundle SDK vào fork này.
// Kiến trúc: plans/20260730-0841-bulbal-chat-realtimekit-call/plan.md (repo HRM).

import { BULBAL_HRM_CALL_CREATE_URL, BULBAL_CALL_FETCH_TIMEOUT_MS } from './bulbal-config.js';

// Tạo meeting trên server. Trả Promise<{meetingId, meetingUrl}>; throw Error(message tiếng Việt).
export function createBulbalCall(title) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), BULBAL_CALL_FETCH_TIMEOUT_MS);
  return fetch(BULBAL_HRM_CALL_CREATE_URL, {
      method: 'POST',
      credentials: 'include', // cookie session khác subdomain (hrm.bulbalvn.com).
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title }),
      signal: controller.signal,
    })
    .then(resp => resp.json().catch(() => ({})).then(data => {
      if (!resp.ok || !data.meetingUrl) {
        throw new Error(data.error || 'Không tạo được cuộc gọi, vui lòng thử lại');
      }
      return { meetingId: data.meetingId, meetingUrl: data.meetingUrl };
    }))
    .finally(() => clearTimeout(timer));
}

// Tin nhắn mời dạng drafty: "<người gọi> đã bắt đầu cuộc gọi · Bấm để tham gia" — cụm cuối là link.
// Dựng tay theo spec drafty (ổn định) thay vì phụ thuộc helper SDK.
// ⚠️ KHÔNG THÊM EMOJI vào txt: JS đếm at/len theo UTF-16 (emoji = 2), Go server đếm theo
// code point (1) → lệch chỉ số → server "could not parse payload" → PUSH FCM CHẾT CÂM cho
// mọi tin mời (bug 260730, 14 push fail). Icon 📞 do CSS ::before của thẻ gọi hiển thị.
// Tiếng Việt có dấu an toàn (BMP: 1 ký tự = 1 đơn vị ở cả hai hệ).
export function buildCallInviteDrafty(callerName, meetingUrl, audioOnly) {
  const prefix = `${callerName} đã bắt đầu cuộc gọi${audioOnly ? ' thoại' : ' video'} · `;
  const linkText = 'Bấm để tham gia';
  return {
    txt: prefix + linkText,
    fmt: [{ at: prefix.length, len: linkText.length, key: 0 }],
    ent: [{ tp: 'LN', data: { url: meetingUrl } }],
  };
}

// ===== Overlay nhúng cuộc gọi trong màn hình chat (thay vì mở tab) =====
// Traefik đã gỡ X-Frame-Options cho /call/* + CSP frame-ancestors chỉ cho chat.bulbalvn.com.
// Đóng overlay theo 3 đường: (1) nút ✕; (2) iframe load lần 2 = trang /call điều hướng đi
// (người dùng bấm "rời cuộc gọi" trong RtkMeeting → page tự về trang chủ HRM) — heuristic
// cross-origin an toàn; (3) postMessage 'bulbal:call-left' từ page (kích hoạt sau lần build
// HRM kế tiếp — hiện là dự phòng tương lai).

const OVERLAY_ID = 'bulbal-call-overlay';
const COMPACT_W = 360, COMPACT_H = 560;

function overlayBaseStyle(el, compact) {
  const s = el.style;
  s.position = 'fixed';
  s.zIndex = '200'; // trên menu ngữ cảnh (z30) và header
  s.background = '#0a0a0a';
  s.display = 'flex';
  s.flexDirection = 'column';
  s.boxShadow = '0 8px 40px rgba(0,0,0,.55)';
  if (compact) {
    s.inset = 'auto 16px 16px auto';
    s.width = COMPACT_W + 'px';
    s.height = COMPACT_H + 'px';
    s.borderRadius = '14px';
    s.overflow = 'hidden';
  } else {
    s.inset = '0';
    s.width = 'auto';
    s.height = 'auto';
    s.borderRadius = '0';
  }
}

export function closeCallOverlay() {
  const el = document.getElementById(OVERLAY_ID);
  if (el) {
    el.remove();
  }
}

// Gán URL cho overlay đang ở trạng thái spinner (A1: overlay mở TRƯỚC, meeting tạo SAU).
export function setCallOverlayUrl(meetingUrl) {
  const wrap = document.getElementById(OVERLAY_ID);
  if (!wrap || !wrap._bulbalMountIframe) {
    openCallOverlay(meetingUrl);
    return;
  }
  wrap._bulbalMountIframe(meetingUrl);
}

// meetingUrl BỎ TRỐNG được → overlay hiện spinner "Đang tạo cuộc gọi…" ngay trong cú click
// (phản hồi 0ms), sau đó setCallOverlayUrl(url) khi server trả về.
export function openCallOverlay(meetingUrl) {
  closeCallOverlay(); // singleton — mở cuộc mới thay cuộc cũ
  const isMobile = window.innerWidth < 700;
  let compact = false;

  const wrap = document.createElement('div');
  wrap.id = OVERLAY_ID;
  overlayBaseStyle(wrap, false);

  const bar = document.createElement('div');
  bar.style.cssText = 'display:flex;align-items:center;justify-content:space-between;' +
    'padding:6px 10px;background:#141414;color:#e5e5e5;font-size:13px;flex:0 0 auto;' +
    'font-family:inherit;user-select:none;';
  const title = document.createElement('span');
  title.textContent = '📞 Cuộc gọi Bulbal';
  const btns = document.createElement('span');
  const mkBtn = (label, titleText, onClick) => {
    const b = document.createElement('button');
    b.textContent = label;
    b.title = titleText;
    b.style.cssText = 'background:none;border:none;color:#e5e5e5;cursor:pointer;' +
      'font-size:15px;padding:4px 8px;line-height:1;';
    b.onclick = onClick;
    return b;
  };
  if (!isMobile) {
    btns.appendChild(mkBtn('⿻', 'Thu nhỏ / phóng to', () => {
      compact = !compact;
      overlayBaseStyle(wrap, compact);
    }));
  }
  btns.appendChild(mkBtn('✕', 'Đóng (rời cuộc gọi)', closeCallOverlay));
  bar.appendChild(title);
  bar.appendChild(btns);

  // Vùng nội dung: spinner trước, iframe thế chỗ khi có URL.
  const content = document.createElement('div');
  content.style.cssText = 'flex:1 1 auto;display:flex;align-items:center;justify-content:center;';
  const spinner = document.createElement('div');
  spinner.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:14px;color:#a3a3a3;font-size:14px;';
  const ring = document.createElement('div');
  ring.style.cssText = 'width:38px;height:38px;border:4px solid #333;border-top-color:#2E6BE6;' +
    'border-radius:50%;animation:bulbal-spin .9s linear infinite;';
  const spinLabel = document.createElement('span');
  spinLabel.textContent = 'Đang tạo cuộc gọi…';
  spinner.appendChild(ring);
  spinner.appendChild(spinLabel);
  ensureSpinKeyframes();
  content.appendChild(spinner);

  wrap._bulbalMountIframe = (url) => {
    const iframe = document.createElement('iframe');
    iframe.src = url;
    iframe.allow = 'camera; microphone; autoplay; display-capture; picture-in-picture';
    iframe.allowFullscreen = true;
    iframe.style.cssText = 'border:0;width:100%;height:100%;';
    let loadCount = 0;
    iframe.addEventListener('load', () => {
      loadCount += 1;
      if (loadCount >= 2) closeCallOverlay(); // trang /call điều hướng đi = đã rời cuộc gọi
    });
    content.replaceChildren(iframe);
  };
  if (meetingUrl) {
    wrap._bulbalMountIframe(meetingUrl);
  }

  wrap.appendChild(bar);
  wrap.appendChild(content);
  document.body.appendChild(wrap);
}

function ensureSpinKeyframes() {
  if (document.getElementById('bulbal-spin-kf')) return;
  const st = document.createElement('style');
  st.id = 'bulbal-spin-kf';
  st.textContent = '@keyframes bulbal-spin{to{transform:rotate(360deg)}}';
  document.head.appendChild(st);
}

// Chặn click vào link mời "Bấm để tham gia" trong tin nhắn → mở overlay thay vì tab mới.
// Gọi MỘT lần từ tinode-web componentDidMount.
export function installCallLinkInterceptor() {
  document.addEventListener('click', evt => {
    const anchor = evt.target && evt.target.closest ?
      evt.target.closest('a[href^="https://hrm.bulbalvn.com/call/"]') : null;
    if (anchor) {
      evt.preventDefault();
      evt.stopPropagation();
      openCallOverlay(anchor.href);
    }
  }, true);
}

// postMessage từ page /call (sau lần build HRM kế tiếp) — dự phòng đóng overlay chủ động.
window.addEventListener('message', evt => {
  if (evt.origin === 'https://hrm.bulbalvn.com' && evt.data && evt.data.type === 'bulbal:call-left') {
    closeCallOverlay();
  }
});

// ===== Đổ chuông phía người nghe (tab đang mở) =====
// Tin mời mang head.bulbal_call='invite' (publish từ handleBulbalCall). Client nhận {data}
// của topic đang attach → banner chuông + audio/call-in.m4a. Giới hạn đã biết: chỉ ring khi
// người nghe đang mở ĐÚNG hội thoại đó (onDataMessage chỉ phủ topic attached); topic khác →
// beep tin nhắn thường + badge; tab đóng → FCM push. Ring toàn cục cần server hỗ trợ — không làm.

const RING_BANNER_ID = 'bulbal-ring-banner';
const RING_SOUND_IN = new Audio('audio/call-in.m4a');
RING_SOUND_IN.loop = true;
const RING_AUTO_DISMISS_MS = 45000;
const RING_FRESH_WINDOW_MS = 45000;

let ringVibrateTimer = null;

function dismissRingBanner() {
  RING_SOUND_IN.pause();
  RING_SOUND_IN.currentTime = 0;
  if (ringVibrateTimer) {
    clearInterval(ringVibrateTimer);
    ringVibrateTimer = null;
  }
  if (navigator.vibrate) {
    navigator.vibrate(0); // tắt rung còn dở
  }
  const el = document.getElementById(RING_BANNER_ID);
  if (el) {
    el.remove();
  }
}

// Vòng tròn chữ cái đầu (không phụ thuộc avatar mạng — hiện tức thì).
function makeInitialCircle(name, sizePx) {
  const circle = document.createElement('div');
  const initial = (name || '?').trim().split(/\s+/).pop().charAt(0).toUpperCase();
  circle.textContent = initial;
  circle.style.cssText = `width:${sizePx}px;height:${sizePx}px;border-radius:50%;background:#2E6BE6;` +
    `color:#fff;display:flex;align-items:center;justify-content:center;flex:0 0 auto;` +
    `font-size:${Math.round(sizePx * 0.42)}px;font-weight:600;`;
  return circle;
}

function ringButton(label, bg, minH, onClick) {
  const b = document.createElement('button');
  b.textContent = label;
  b.style.cssText = `background:${bg};color:#fff;border:none;border-radius:12px;` +
    `padding:10px 22px;cursor:pointer;font-size:15px;font-weight:600;min-height:${minH}px;`;
  b.onclick = onClick;
  return b;
}

// Desktop: banner nổi top-center. Mobile (<700px): MÀN HÌNH NHẬN CUỘC GỌI FULLSCREEN kiểu Zalo
// (avatar lớn + tên + 2 nút tròn), kèm rung. Đều dùng chuông audio/call-in.m4a loop.
function showRingBanner(callerName, meetingUrl) {
  dismissRingBanner();
  const isMobile = window.innerWidth < 700;
  const wrap = document.createElement('div');
  wrap.id = RING_BANNER_ID;

  const answer = () => {
    dismissRingBanner();
    openCallOverlay(meetingUrl);
  };

  if (isMobile) {
    wrap.style.cssText = 'position:fixed;inset:0;z-index:210;background:#0a0a0aF2;color:#f5f5f5;' +
      'display:flex;flex-direction:column;align-items:center;justify-content:center;gap:18px;' +
      'padding:32px calc(16px + env(safe-area-inset-right)) calc(40px + env(safe-area-inset-bottom)) calc(16px + env(safe-area-inset-left));';
    wrap.appendChild(makeInitialCircle(callerName, 108));
    const name = document.createElement('div');
    name.textContent = callerName;
    name.style.cssText = 'font-size:22px;font-weight:700;text-align:center;';
    const sub = document.createElement('div');
    sub.textContent = 'Cuộc gọi Bulbal đến…';
    sub.style.cssText = 'font-size:14px;color:#a3a3a3;';
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:56px;margin-top:26px;';
    row.appendChild(ringButton('Từ chối', '#dc2626', 56, dismissRingBanner));
    row.appendChild(ringButton('Nghe', '#16a34a', 56, answer));
    wrap.appendChild(name);
    wrap.appendChild(sub);
    wrap.appendChild(row);
  } else {
    wrap.style.cssText = 'position:fixed;top:14px;left:50%;transform:translateX(-50%);z-index:210;' +
      'display:flex;align-items:center;gap:14px;background:#141414;color:#f5f5f5;' +
      'padding:12px 18px;border-radius:16px;box-shadow:0 8px 30px rgba(0,0,0,.5);' +
      'font-size:14px;max-width:92vw;';
    wrap.appendChild(makeInitialCircle(callerName, 44));
    const label = document.createElement('span');
    label.textContent = `${callerName} đang gọi bạn…`;
    wrap.appendChild(label);
    wrap.appendChild(ringButton('Nghe', '#16a34a', 44, answer));
    wrap.appendChild(ringButton('Bỏ qua', '#333', 44, dismissRingBanner));
  }

  document.body.appendChild(wrap);
  RING_SOUND_IN.play().catch(_ => { /* autoplay policy — banner vẫn hiện */ });
  // Rung LẶP suốt lúc đổ chuông (như chuông thật). Giới hạn nền tảng đã biết:
  // iOS Safari KHÔNG hỗ trợ vibrate (không bao giờ rung); Android Chrome cần người dùng
  // đã chạm trang ≥1 lần trong phiên (sticky activation) — chưa chạm thì bị nuốt câm.
  if (navigator.vibrate) {
    const buzz = () => navigator.vibrate([400, 250, 400, 250, 600]);
    buzz();
    ringVibrateTimer = setInterval(buzz, 2600);
  }
  setTimeout(() => {
    if (document.getElementById(RING_BANNER_ID) === wrap) {
      dismissRingBanner();
    }
  }, RING_AUTO_DISMISS_MS);
}

// ===== Probe chuông TOÀN CỤC (thử nghiệm, công tắc BULBAL_GLOBAL_RING_PROBE) =====
// Vấn đề: client chỉ nhận {data} của topic đang attach → tin mời ở hội thoại KHÁC không reo.
// Giải client-only: khi me-topic báo 'msg' cho topic không mở → subscribe kéo 1 tin mới nhất
// rồi leave(false) ngay (~2 gói tin). Tin kéo về chảy qua onDataMessage → maybeRingForCallInvite
// tự reo nếu là tin mời. leave(false) BẮT BUỘC — leave(true) là HỦY ĐĂNG KÝ topic (phá hoại).
// Rủi ro chấp nhận: cửa sổ attach ~1,5s có thể đụng người dùng mở đúng topic cùng lúc (hiếm,
// nội bộ); throttle 10s/topic chống bão.

const PROBE_THROTTLE_MS = 10000;
const probeLastAt = new Map();

export function probeTopicForCallInvite(tinode, topicName, currentTopicSelected, enabled) {
  if (!enabled || !topicName || topicName === currentTopicSelected) return;
  if (topicName === 'me' || topicName === 'fnd' || topicName === 'sys') return;
  const last = probeLastAt.get(topicName) || 0;
  if (Date.now() - last < PROBE_THROTTLE_MS) return;
  probeLastAt.set(topicName, Date.now());
  try {
    const topic = tinode.getTopic(topicName);
    if (!topic || topic.isSubscribed()) return; // đang attach nơi khác — data tự chảy về rồi
    if (topic.isMuted && topic.isMuted()) return; // đã tắt thông báo — tôn trọng, không reo
    if (topic.isArchived && topic.isArchived()) return;
    topic
      .subscribe(topic.startMetaQuery().withLaterData(1).build())
      .catch(_ => { /* topic không vào được — thôi */ })
      .finally(() => {
        setTimeout(() => {
          try {
            if (topic.isSubscribed() && topicName !== currentTopicSelected) {
              topic.leave(false); // false = CHỈ detach phiên, KHÔNG unsubscribe
            }
          } catch (_) { /* ignore */ }
        }, 1500);
      });
  } catch (_) { /* ignore — probe là tính năng phụ, không được phá chat */ }
}

// Gọi từ tinode-web.handleDataMessage cho MỌI {data} đến — tự lọc, không side-effect khi không khớp.
export function maybeRingForCallInvite(data, myUserId) {
  if (!data || !data.head || data.head.bulbal_call !== 'invite') return;
  if (data.from === myUserId) return;
  const ts = data.ts instanceof Date ? data.ts.getTime() : (data.ts ? new Date(data.ts).getTime() : 0);
  if (!ts || Date.now() - ts > RING_FRESH_WINDOW_MS) return; // tin cũ (load lịch sử/reconnect) — im
  const ent = data.content && data.content.ent && data.content.ent[0];
  const url = ent && ent.data && ent.data.url;
  if (!url || !url.startsWith('https://hrm.bulbalvn.com/call/')) return;
  // Tên người gọi lấy từ chính nội dung tin "<tên> đã bắt đầu..." — khỏi tra topic.
  // (Tin không còn emoji mở đầu — xem ghi chú buildCallInviteDrafty.)
  const txt = (data.content.txt || '');
  const callerName = txt.split(' đã bắt đầu')[0] || 'Đồng nghiệp';
  showRingBanner(callerName, url);
}
